import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTimes, FaBox, FaWeight, FaHistory, FaTrash, FaClipboardList, FaCheck, FaUser, FaClock, FaMinus, FaChartBar, FaChartLine, FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  doc, 
  query, 
  orderBy,
  where,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../services/firebase';

const RoasterInventoryPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory', 'requests', 'logs', or 'analytics'
  const [inventoryItems, setInventoryItems] = useState([]);
  const [beansRequests, setBeansRequests] = useState([]);
  const [inventoryLogs, setInventoryLogs] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Add Item Form State
  const [newItem, setNewItem] = useState({
    beanName: '',
    unit: 'kg',
    unitSize: ''
  });

  // Edit Item Form State
  const [editForm, setEditForm] = useState({
    action: 'add', // 'add' or 'remove'
    quantity: '',
    note: ''
  });

  const units = [
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'g', label: 'Grams (g)' },
    { value: 'pack', label: 'Packs' }
  ];

  // Add roaster-page class to body to disable header gradient and reset margins
  useEffect(() => {
    document.body.classList.add('roaster-page');
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    return () => {
      document.body.classList.remove('roaster-page');
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
    };
  }, []);

  // Fetch inventory items
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const q = query(
          collection(db, 'roasterInventory'),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setInventoryItems(items);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching inventory:', error);
        setError('Failed to load inventory items');
      }
    };

    fetchInventory();
  }, []);

  // Fetch beans requests
  useEffect(() => {
    const fetchBeansRequests = async () => {
      try {
        const q = query(
          collection(db, 'beansRequests'),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setBeansRequests(requests);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching beans requests:', error);
        setError('Failed to load beans requests');
      }
    };

    fetchBeansRequests();
  }, []);

  // Fetch inventory logs
  useEffect(() => {
    const fetchInventoryLogs = async () => {
      try {
        const q = query(
          collection(db, 'inventoryLogs'),
          orderBy('timestamp', 'desc')
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const logs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setInventoryLogs(logs);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching inventory logs:', error);
        setError('Failed to load inventory logs');
      }
    };

    fetchInventoryLogs();
  }, []);

  const resetAddForm = () => {
    setNewItem({ beanName: '', unit: 'kg', unitSize: '' });
    setShowAddModal(false);
  };

  const resetEditForm = () => {
    setEditForm({ action: 'add', quantity: '', note: '' });
    setSelectedItem(null);
    setShowEditModal(false);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const itemData = {
        beanName: newItem.beanName.trim(),
        unit: newItem.unit,
        unitSize: parseFloat(newItem.unitSize),
        quantity: 0,
        createdAt: new Date(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        lastUpdated: new Date(),
        active: true
      };

      const docRef = await addDoc(collection(db, 'roasterInventory'), itemData);
      
      // Log the creation with proper item ID
      await addDoc(collection(db, 'inventoryLogs'), {
        itemId: docRef.id,
        itemName: newItem.beanName.trim(),
        action: 'created',
        previousQuantity: 0,
        newQuantity: 0,
        changeAmount: 0,
        unit: newItem.unit,
        unitSize: parseFloat(newItem.unitSize),
        note: `New bean type added to inventory: ${newItem.beanName.trim()} (${newItem.unitSize}${newItem.unit} per unit)`,
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        performedByRole: currentUser.role || 'Roaster',
        timestamp: new Date(),
        createdAt: new Date()
      });

      setSuccess('Item added successfully!');
      resetAddForm();
    } catch (error) {
      console.error('Error adding item:', error);
      setError('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuantity = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const changeAmount = parseFloat(editForm.quantity);
      const currentQuantity = selectedItem.quantity || 0;
      
      let newQuantity;
      if (editForm.action === 'add') {
        newQuantity = currentQuantity + changeAmount;
      } else {
        newQuantity = Math.max(0, currentQuantity - changeAmount);
      }

      // Update inventory item
      await updateDoc(doc(db, 'roasterInventory', selectedItem.id), {
        quantity: newQuantity,
        lastUpdated: new Date(),
        lastUpdatedBy: currentUser.uid,
        lastUpdatedByName: currentUser.displayName || currentUser.email
      });

      // Log the change with detailed information
      await addDoc(collection(db, 'inventoryLogs'), {
        itemId: selectedItem.id,
        itemName: selectedItem.beanName,
        action: editForm.action,
        previousQuantity: currentQuantity,
        newQuantity: newQuantity,
        changeAmount: editForm.action === 'add' ? changeAmount : -changeAmount,
        unit: selectedItem.unit,
        unitSize: selectedItem.unitSize,
        note: editForm.note.trim() || `${editForm.action === 'add' ? 'Added' : 'Removed'} ${changeAmount} units`,
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        performedByRole: currentUser.role || 'Roaster',
        timestamp: new Date(),
        createdAt: new Date(),
        logType: 'manual_adjustment'
      });

      setSuccess(`Successfully ${editForm.action === 'add' ? 'added' : 'removed'} ${changeAmount} units`);
      resetEditForm();
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError('Failed to update quantity');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleRemoveItem = async (item) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${item.beanName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Delete the item from Firestore
      await deleteDoc(doc(db, 'roasterInventory', item.id));

      // Log the deletion with detailed information
      await addDoc(collection(db, 'inventoryLogs'), {
        itemId: item.id,
        itemName: item.beanName,
        action: 'deleted',
        previousQuantity: item.quantity || 0,
        newQuantity: 0,
        changeAmount: -(item.quantity || 0),
        unit: item.unit,
        unitSize: item.unitSize,
        note: `Item permanently deleted: ${item.beanName} (${item.quantity || 0} units removed from inventory)`,
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        performedByRole: currentUser.role || 'Roaster',
        timestamp: new Date(),
        createdAt: new Date(),
        logType: 'deletion'
      });

      setSuccess(`Successfully deleted "${item.beanName}"`);
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestStatusUpdate = async (requestId, newStatus) => {
    setLoading(true);
    setError(null);

    try {
      // Find the request to get details
      const request = beansRequests.find(req => req.id === requestId);
      if (!request) {
        setError('Request not found');
        setLoading(false);
        return;
      }

      // Update request status
      await updateDoc(doc(db, 'beansRequests', requestId), {
        status: newStatus,
        updatedAt: new Date(),
        processedBy: currentUser.uid,
        processedByName: currentUser.displayName || currentUser.email
      });

      // If approved, automatically deduct from inventory
      if (newStatus === 'approved' && request.beanId) {
        // Find the inventory item
        const inventoryItem = inventoryItems.find(item => item.id === request.beanId);
        if (inventoryItem) {
          const currentQuantity = inventoryItem.quantity || 0;
          const requestedQuantity = request.requestedQuantity || 0;
          const newQuantity = currentQuantity - requestedQuantity;

          // Update inventory quantity (can go negative)
          await updateDoc(doc(db, 'roasterInventory', request.beanId), {
            quantity: newQuantity,
            lastUpdated: new Date(),
            lastUpdatedBy: currentUser.uid,
            lastUpdatedByName: currentUser.displayName || currentUser.email
          });

          // Log the inventory change with detailed information
          await addDoc(collection(db, 'inventoryLogs'), {
            itemId: request.beanId,
            itemName: request.beanName,
            action: 'remove',
            previousQuantity: currentQuantity,
            newQuantity: newQuantity,
            changeAmount: -requestedQuantity,
            unit: inventoryItem.unit,
            unitSize: inventoryItem.unitSize,
            note: `Approved request from ${request.requestedByName} (${request.requestedByRole}) - ${requestedQuantity} units deducted${request.notes ? ` | Request notes: ${request.notes}` : ''}`,
            performedBy: currentUser.uid,
            performedByName: currentUser.displayName || currentUser.email,
            performedByRole: currentUser.role || 'Roaster',
            timestamp: new Date(),
            createdAt: new Date(),
            logType: 'request_approval',
            relatedRequestId: requestId,
            requestedBy: request.requestedBy,
            requestedByName: request.requestedByName,
            requestedByRole: request.requestedByRole
          });

          setSuccess(`Request approved and ${requestedQuantity} units deducted from inventory!`);
        } else {
          setSuccess(`Request approved! (No inventory item found to deduct from)`);
        }
      } else {
        setSuccess(`Request ${newStatus} successfully!`);
      }
    } catch (error) {
      console.error('Error updating request status:', error);
      setError('Failed to update request status');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdjustment = async (item, adjustment) => {
    setLoading(true);
    setError(null);

    try {
      const currentQuantity = item.quantity || 0;
      const newQuantity = Math.max(0, currentQuantity + adjustment);

      // Update inventory item
      await updateDoc(doc(db, 'roasterInventory', item.id), {
        quantity: newQuantity,
        lastUpdated: new Date(),
        lastUpdatedBy: currentUser.uid,
        lastUpdatedByName: currentUser.displayName || currentUser.email
      });

      // Log the change
      await addDoc(collection(db, 'inventoryLogs'), {
        itemId: item.id,
        itemName: item.beanName,
        action: adjustment > 0 ? 'add' : 'remove',
        previousQuantity: currentQuantity,
        newQuantity: newQuantity,
        changeAmount: adjustment,
        unit: item.unit,
        unitSize: item.unitSize,
        note: `Quick adjustment: ${adjustment > 0 ? 'Added' : 'Removed'} ${Math.abs(adjustment)} unit${Math.abs(adjustment) !== 1 ? 's' : ''}`,
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        performedByRole: currentUser.role || 'Roaster',
        timestamp: new Date(),
        createdAt: new Date(),
        logType: 'quick_adjustment'
      });

      setSuccess(`Successfully ${adjustment > 0 ? 'added' : 'removed'} ${Math.abs(adjustment)} unit${Math.abs(adjustment) !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError('Failed to update quantity');
    } finally {
      setLoading(false);
    }
  };

  // Analytics functions
  const calculateAnalytics = () => {
    const now = new Date();
    const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Most requested beans
    const beanRequestCounts = {};
    beansRequests.forEach(request => {
      if (request.createdAt && request.createdAt.toDate() >= lastMonth) {
        beanRequestCounts[request.beanName] = (beanRequestCounts[request.beanName] || 0) + request.requestedQuantity;
      }
    });

    // Most active users
    const userRequestCounts = {};
    beansRequests.forEach(request => {
      if (request.createdAt && request.createdAt.toDate() >= lastMonth) {
        userRequestCounts[request.requestedByName] = (userRequestCounts[request.requestedByName] || 0) + 1;
      }
    });

    // Recent activity analysis
    const recentLogs = inventoryLogs.filter(log => 
      log.timestamp && log.timestamp.toDate() >= lastWeek
    );

    // Low stock items
    const lowStockItems = inventoryItems.filter(item => item.quantity <= 5);

    // Items with high usage (frequent removals)
    const itemUsage = {};
    inventoryLogs.forEach(log => {
      if (log.action === 'remove' && log.timestamp && log.timestamp.toDate() >= lastWeek) {
        itemUsage[log.itemName] = (itemUsage[log.itemName] || 0) + Math.abs(log.changeAmount || 0);
      }
    });

    // Approval rate
    const totalRequests = beansRequests.length;
    const approvedRequests = beansRequests.filter(req => req.status === 'approved').length;
    const approvalRate = totalRequests > 0 ? (approvedRequests / totalRequests * 100).toFixed(1) : 0;

    return {
      mostRequestedBeans: Object.entries(beanRequestCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5),
      mostActiveUsers: Object.entries(userRequestCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5),
      recentActivity: recentLogs.length,
      lowStockItems,
      highUsageItems: Object.entries(itemUsage)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5),
      approvalRate,
      totalRequests,
      pendingRequests: beansRequests.filter(req => req.status === 'pending').length
    };
  };

  const analytics = calculateAnalytics();

  // Reset function to clear all data except beans inventory
  const handleResetAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Clear beans requests
      const requestsSnapshot = await getDocs(collection(db, 'beansRequests'));
      const requestsDeletePromises = requestsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Clear inventory logs
      const logsSnapshot = await getDocs(collection(db, 'inventoryLogs'));
      const logsDeletePromises = logsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Execute all deletions
      await Promise.all([...requestsDeletePromises, ...logsDeletePromises]);
      
      // Add a system log entry for the reset action
      await addDoc(collection(db, 'inventoryLogs'), {
        itemId: 'system',
        itemName: 'System Reset',
        action: 'system_reset',
        previousQuantity: 0,
        newQuantity: 0,
        changeAmount: 0,
        unit: 'system',
        unitSize: 0,
        note: 'System data reset performed - cleared all beans requests and inventory logs (beans inventory preserved)',
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        performedByRole: currentUser.role || 'Roaster',
        timestamp: new Date(),
        createdAt: new Date(),
        logType: 'system_reset'
      });
      
      setSuccess('All data reset successfully! Beans inventory has been preserved.');
      setShowResetConfirm(false);
      
    } catch (error) {
      console.error('Error resetting data:', error);
      setError('Failed to reset data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: '#f8f9fa',
      minHeight: '100vh',
      padding: '0',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      paddingTop: '0'
    }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        padding: '20px',
        margin: '0',
        borderBottom: '1px solid #e9ecef'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <h1 style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '32px',
            fontWeight: '700',
            color: '#2d3748',
            margin: '0',
            letterSpacing: '0.5px'
          }}>Roaster Inventory</h1>
          
          <button
            onClick={() => setShowResetConfirm(true)}
            disabled={loading}
            style={{
              background: '#6c757d',
              color: '#fff',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              opacity: loading ? 0.6 : 1
            }}
            onMouseOver={(e) => {
              if (!loading) e.target.style.background = '#5a6268';
            }}
            onMouseOut={(e) => {
              if (!loading) e.target.style.background = '#6c757d';
            }}
          >
            <FaRedo size={14} />
            Reset All Data
          </button>
        </div>
        
        <div style={{
          fontSize: '14px',
          color: '#6c757d',
          fontFamily: 'Inter, sans-serif'
        }}>
          Manage coffee bean inventory and stock levels
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e9ecef',
        position: 'sticky',
        top: '0',
        zIndex: 10
      }}>
        <div style={{
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}>
          <div style={{
            display: 'flex',
            minWidth: 'fit-content',
            paddingLeft: '20px',
            paddingRight: '20px'
          }}>
            <button
              onClick={() => setActiveTab('inventory')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '16px 20px',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'inventory' ? '#2d3748' : '#6c757d',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                borderBottom: activeTab === 'inventory' ? '2px solid #2d3748' : '2px solid transparent'
              }}
              onTouchStart={(e) => {
                e.target.style.transform = 'scale(0.95)';
              }}
              onTouchEnd={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <FaBox size={14} />
              Inventory
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '16px 20px',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'requests' ? '#2d3748' : '#6c757d',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                borderBottom: activeTab === 'requests' ? '2px solid #2d3748' : '2px solid transparent'
              }}
              onTouchStart={(e) => {
                e.target.style.transform = 'scale(0.95)';
              }}
              onTouchEnd={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <FaClipboardList size={14} />
              Requests
              {beansRequests.filter(req => req.status === 'pending').length > 0 && (
                <span style={{
                  background: '#e53e3e',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: '700',
                  padding: '3px 6px',
                  borderRadius: '12px',
                  minWidth: '20px',
                  textAlign: 'center',
                  lineHeight: '1'
                }}>
                  {beansRequests.filter(req => req.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '16px 20px',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'logs' ? '#2d3748' : '#6c757d',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                borderBottom: activeTab === 'logs' ? '2px solid #2d3748' : '2px solid transparent'
              }}
              onTouchStart={(e) => {
                e.target.style.transform = 'scale(0.95)';
              }}
              onTouchEnd={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <FaHistory size={14} />
              Logs
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '16px 20px',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'analytics' ? '#2d3748' : '#6c757d',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                borderBottom: activeTab === 'analytics' ? '2px solid #2d3748' : '2px solid transparent'
              }}
              onTouchStart={(e) => {
                e.target.style.transform = 'scale(0.95)';
              }}
              onTouchEnd={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <FaChartBar size={14} />
              Analytics
            </button>
          </div>
        </div>
        
        {/* Hide scrollbar for webkit browsers */}
        <style jsx>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          background: '#fed7d7',
          border: '1px solid #feb2b2',
          color: '#c53030',
          padding: '16px 20px',
          margin: '16px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#c53030',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0'
            }}
          >×</button>
        </div>
      )}

      {success && (
        <div style={{
          background: '#c6f6d5',
          border: '1px solid #9ae6b4',
          color: '#276749',
          padding: '16px 20px',
          margin: '16px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{success}</span>
          <button 
            onClick={() => setSuccess(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#276749',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0'
            }}
          >×</button>
        </div>
      )}

      {/* Content based on active tab */}
      <div style={{ background: '#f8f9fa', minHeight: '100vh', padding: '20px' }}>
      {activeTab === 'inventory' ? (
        <>
          {/* Add New Item Button */}
          <div style={{ marginBottom: '20px' }}>
            <button 
              onClick={() => setShowAddModal(true)}
              style={{
                width: '100%',
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                padding: '16px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onTouchStart={(e) => {
                e.target.style.transform = 'scale(0.98)';
                e.target.style.background = '#5a6268';
              }}
              onTouchEnd={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.background = '#6c757d';
              }}
            >
              <FaPlus size={20} />
              Add New Item
            </button>
          </div>

          {/* Inventory List */}
          <div>
        {inventoryItems.length === 0 ? (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            border: '1px solid #e9ecef'
          }}>
            <FaBox size={48} style={{ color: '#cbd5e0', marginBottom: '16px' }} />
            <h3 style={{
              color: '#4a5568',
              fontSize: '18px',
              fontWeight: '600',
              margin: '0 0 8px 0'
            }}>No inventory items yet</h3>
            <p style={{
              color: '#718096',
              fontSize: '14px',
              margin: '0'
            }}>Tap "Add New Item" to get started</p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {inventoryItems.map((item, index) => (
              <div key={item.id} style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                color: '#2d3748'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2d3748',
                      margin: '0 0 4px 0'
                    }}>{item.beanName}</h3>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <FaWeight style={{ color: '#6c757d', fontSize: '14px' }} />
                      <span style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: item.quantity <= 5 ? '#dc3545' : '#28a745'
                      }}>{item.quantity || 0}</span>
                      <span style={{
                        fontSize: '16px',
                        color: '#6c757d',
                        fontWeight: '500'
                      }}>units</span>
                      <span style={{
                        fontSize: '14px',
                        color: '#adb5bd',
                        fontWeight: '400'
                      }}>({item.unitSize}{item.unit} each)</span>
                    </div>
                    
                    {/* Quick adjustment buttons */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <button 
                        onClick={() => handleQuickAdjustment(item, -1)}
                        disabled={loading || item.quantity <= 0}
                        style={{
                          background: '#fff',
                          color: '#6c757d',
                          border: '1px solid #dee2e6',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          cursor: loading || item.quantity <= 0 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '500',
                          opacity: loading || item.quantity <= 0 ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                          minWidth: '32px',
                          height: '32px'
                        }}
                        title="Remove 1 unit"
                      >
                        <FaMinus size={10} />
                      </button>
                      <span style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: '500',
                        minWidth: '80px',
                        textAlign: 'center'
                      }}>Quick adjust</span>
                      <button 
                        onClick={() => handleQuickAdjustment(item, 1)}
                        disabled={loading}
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: '#fff',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          padding: '6px 8px',
                          borderRadius: '8px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          opacity: loading ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                          minWidth: '32px',
                          height: '32px'
                        }}
                        title="Add 1 unit"
                      >
                        <FaPlus size={10} />
                      </button>
                    </div>
                    {item.quantity <= 5 && (
                      <div style={{
                        background: 'rgba(255, 235, 59, 0.2)',
                        color: '#ffeb3b',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'inline-block',
                        border: '1px solid rgba(255, 235, 59, 0.3)'
                      }}>
                        ⚠️ Low Stock
                      </div>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    alignItems: 'flex-end'
                  }}>
                    <button 
                      onClick={() => openEditModal(item)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        minWidth: '70px',
                        justifyContent: 'center'
                      }}
                      onTouchStart={(e) => {
                        e.target.style.transform = 'scale(0.95)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                      }}
                      onTouchEnd={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                      }}
                    >
                      <FaEdit size={11} />
                      Edit
                    </button>
                    <button 
                      onClick={() => handleRemoveItem(item)}
                      disabled={loading}
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        minWidth: '70px',
                        justifyContent: 'center',
                        opacity: loading ? 0.6 : 1
                      }}
                      onTouchStart={(e) => {
                        if (!loading) {
                          e.target.style.transform = 'scale(0.95)';
                          e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                        }
                      }}
                      onTouchEnd={(e) => {
                        if (!loading) {
                          e.target.style.transform = 'scale(1)';
                          e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                        }
                      }}
                    >
                      <FaTrash size={11} />
                      Delete
                    </button>
                  </div>
                </div>
                {item.lastUpdated && (
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                    paddingTop: '8px'
                  }}>
                    Last updated: {new Date(item.lastUpdated.toDate()).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      ) : activeTab === 'requests' ? (
        /* Requests Tab */
        <div style={{ padding: '20px' }}>
          {/* Analytics Dashboard */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#f59e0b',
                marginBottom: '4px'
              }}>
                {beansRequests.filter(req => req.status === 'pending').length}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Pending
              </div>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#10b981',
                marginBottom: '4px'
              }}>
                {beansRequests.filter(req => req.status === 'approved').length}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Approved
              </div>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#ef4444',
                marginBottom: '4px'
              }}>
                {beansRequests.filter(req => req.status === 'rejected').length}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Rejected
              </div>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e9ecef',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                {beansRequests.length}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Total
              </div>
            </div>
          </div>

          {/* Requests List */}
          {beansRequests.length === 0 ? (
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '40px 20px',
              textAlign: 'center',
              border: '1px solid #e9ecef'
            }}>
              <FaClipboardList size={48} style={{ color: '#cbd5e0', marginBottom: '16px' }} />
              <h3 style={{
                color: '#4a5568',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 8px 0'
              }}>No requests yet</h3>
              <p style={{
                color: '#718096',
                fontSize: '14px',
                margin: '0'
              }}>Beans requests from baristas and staff will appear here</p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {beansRequests.map((request) => (
                <div key={request.id} style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#2d3748',
                        margin: '0 0 4px 0'
                      }}>{request.beanName}</h3>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        color: '#718096'
                      }}>
                        <FaUser size={12} />
                        {request.requestedByName} ({request.requestedByRole})
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        background: request.status === 'pending' ? '#fef3c7' : 
                                   request.status === 'approved' ? '#d1fae5' : '#fee2e2',
                        color: request.status === 'pending' ? '#92400e' : 
                               request.status === 'approved' ? '#065f46' : '#991b1b',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {request.status}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: '14px', color: '#718096' }}>Requested:</span>
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                      {request.requestedQuantity} {request.unitSize ? `units (${request.unitSize}${request.unit} each)` : request.unit}
                    </span>
                  </div>

                  {/* Inventory Status Indicator */}
                  {request.beanId ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px', color: '#718096' }}>Available in inventory:</span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: request.availableQuantity >= request.requestedQuantity ? '#10b981' : '#f59e0b'
                      }}>
                        {request.availableQuantity || 0} units
                      </span>
                      {request.status === 'pending' && (
                        <span style={{
                          fontSize: '12px',
                          color: '#8b5a3c',
                          fontStyle: 'italic'
                        }}>
                          (Will auto-deduct if approved)
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px', color: '#718096' }}>Type:</span>
                      <span style={{
                        fontSize: '12px',
                        background: '#fef3c7',
                        color: '#92400e',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '600'
                      }}>
                        Custom Request
                      </span>
                      <span style={{
                        fontSize: '12px',
                        color: '#8b5a3c',
                        fontStyle: 'italic'
                      }}>
                        (No inventory deduction)
                      </span>
                    </div>
                  )}

                  {request.notes && (
                    <div style={{
                      background: '#f7fafc',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#4a5568',
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>Notes:</div>
                      <div style={{
                        fontSize: '14px',
                        color: '#2d3748'
                      }}>{request.notes}</div>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '12px',
                    color: '#a0aec0',
                    marginBottom: '12px'
                  }}>
                    <FaClock size={10} />
                    {request.createdAt && new Date(request.createdAt.toDate()).toLocaleString()}
                  </div>

                  {request.status === 'pending' && (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      borderTop: '1px solid #f7fafc',
                      paddingTop: '12px'
                    }}>
                      <button
                        onClick={() => handleRequestStatusUpdate(request.id, 'approved')}
                        disabled={loading}
                        style={{
                          flex: 1,
                          background: '#10b981',
                          color: '#fff',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          opacity: loading ? 0.6 : 1
                        }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handleRequestStatusUpdate(request.id, 'rejected')}
                        disabled={loading}
                        style={{
                          flex: 1,
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          opacity: loading ? 0.6 : 1
                        }}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'logs' ? (
        /* Activity Logs Tab */
        <div style={{ padding: '20px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#2d3748',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FaHistory style={{ color: '#8b5a3c' }} />
            Activity Logs
          </h2>
          
          {inventoryLogs.length === 0 ? (
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '40px 20px',
              textAlign: 'center',
              border: '1px solid #e9ecef'
            }}>
              <FaHistory size={48} style={{ color: '#cbd5e0', marginBottom: '16px' }} />
              <h3 style={{
                color: '#4a5568',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 8px 0'
              }}>No activity logs yet</h3>
              <p style={{
                color: '#718096',
                fontSize: '14px',
                margin: '0'
              }}>All inventory changes and actions will be logged here</p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {inventoryLogs.map((log) => (
                <div key={log.id} style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        background: 
                          log.action === 'created' ? '#e6fffa' :
                          log.action === 'add' ? '#d1fae5' :
                          log.action === 'remove' ? '#fee2e2' :
                          log.action === 'deleted' ? '#fdf2f8' : '#f7fafc',
                        color: 
                          log.action === 'created' ? '#0d9488' :
                          log.action === 'add' ? '#059669' :
                          log.action === 'remove' ? '#dc2626' :
                          log.action === 'deleted' ? '#be185d' : '#374151',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {log.action === 'created' ? 'Created' :
                         log.action === 'add' ? 'Added' :
                         log.action === 'remove' ? 'Removed' :
                         log.action === 'deleted' ? 'Deleted' : log.action}
                      </div>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2d3748'
                      }}>{log.itemName}</span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#a0aec0'
                    }}>
                      {log.timestamp && new Date(log.timestamp.toDate()).toLocaleString()}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      fontSize: '14px',
                      color: '#4a5568'
                    }}>
                      {log.action !== 'created' && (
                        <>
                          <span>
                            <strong>Before:</strong> {log.previousQuantity} units
                          </span>
                          <span>
                            <strong>After:</strong> {log.newQuantity} units
                          </span>
                          <span style={{
                            color: log.changeAmount > 0 ? '#059669' : '#dc2626',
                            fontWeight: '600'
                          }}>
                            {log.changeAmount > 0 ? '+' : ''}{log.changeAmount}
                          </span>
                        </>
                      )}
                      {log.unitSize && (
                        <span style={{ fontSize: '12px', color: '#718096' }}>
                          ({log.unitSize}{log.unit} per unit)
                        </span>
                      )}
                    </div>
                  </div>

                  {log.note && (
                    <div style={{
                      background: '#f7fafc',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#4a5568',
                      marginBottom: '8px'
                    }}>
                      {log.note}
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#718096',
                    borderTop: '1px solid #f7fafc',
                    paddingTop: '8px'
                  }}>
                    <span>
                      <FaUser size={10} style={{ marginRight: '4px' }} />
                      {log.performedByName} ({log.performedByRole || 'Unknown'})
                    </span>
                    {log.logType && (
                      <span style={{
                        background: '#f1f5f9',
                        color: '#475569',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '500'
                      }}>
                        {log.logType.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'analytics' ? (
        /* Analytics Tab */
        <div style={{ padding: '20px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#2d3748',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FaChartBar style={{ color: '#8b5a3c' }} />
            Smart Analytics
          </h2>
          
          {/* Key Metrics Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#8b5a3c',
                marginBottom: '8px'
              }}>
                {analytics.approvalRate}%
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Approval Rate
              </div>
            </div>
            
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#f59e0b',
                marginBottom: '8px'
              }}>
                {analytics.pendingRequests}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Pending Requests
              </div>
            </div>
            
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#e53e3e',
                marginBottom: '8px'
              }}>
                {analytics.lowStockItems.length}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Low Stock Items
              </div>
            </div>
            
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#10b981',
                marginBottom: '8px'
              }}>
                {analytics.recentActivity}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Recent Activities
              </div>
            </div>
          </div>

          {/* Most Requested Beans */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#2d3748',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaChartLine style={{ color: '#10b981' }} />
              Most Requested Beans (Last 30 Days)
            </h3>
            
            {analytics.mostRequestedBeans.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096'
              }}>
                <FaChartBar size={32} style={{ color: '#cbd5e0', marginBottom: '16px' }} />
                <p style={{ margin: '0', fontSize: '14px' }}>No request data available yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {analytics.mostRequestedBeans.map(([beanName, count], index) => (
                  <div key={beanName} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span style={{
                        background: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#8b5a3c',
                        color: '#fff',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {index + 1}
                      </span>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#2d3748'
                      }}>
                        {beanName}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        width: '100px',
                        height: '6px',
                        background: '#e9ecef',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(count / analytics.mostRequestedBeans[0][1]) * 100}%`,
                          height: '100%',
                          background: '#10b981',
                          borderRadius: '3px'
                        }}></div>
                      </div>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#10b981',
                        minWidth: '40px'
                      }}>
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* High Usage Items & Recommendations */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* High Usage Items */}
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2d3748',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaWeight style={{ color: '#f59e0b' }} />
                High Usage (Last 7 Days)
              </h3>
              
              {analytics.highUsageItems.length === 0 ? (
                <p style={{
                  color: '#718096',
                  fontSize: '14px',
                  textAlign: 'center',
                  margin: '20px 0'
                }}>
                  No usage data available
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {analytics.highUsageItems.map(([itemName, usage]) => (
                    <div key={itemName} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: '#fef3c7',
                      borderRadius: '6px',
                      border: '1px solid #fbbf24'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#92400e'
                      }}>
                        {itemName}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#92400e'
                      }}>
                        -{usage} units
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recommendations */}
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2d3748',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaExclamationTriangle style={{ color: '#e53e3e' }} />
                Recommendations
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {analytics.lowStockItems.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: '#fee2e2',
                    borderRadius: '8px',
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#991b1b',
                      marginBottom: '8px'
                    }}>
                      ⚠️ Restock Alert
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#7f1d1d'
                    }}>
                      {analytics.lowStockItems.length} item{analytics.lowStockItems.length !== 1 ? 's' : ''} running low:
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#7f1d1d',
                      marginTop: '4px'
                    }}>
                      {analytics.lowStockItems.map(item => item.beanName).join(', ')}
                    </div>
                  </div>
                )}
                
                {analytics.mostRequestedBeans.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: '#dbeafe',
                    borderRadius: '8px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1e40af',
                      marginBottom: '8px'
                    }}>
                      📈 Prepare More
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#1e3a8a'
                    }}>
                      Focus on: {analytics.mostRequestedBeans[0][0]} ({analytics.mostRequestedBeans[0][1]} units requested)
                    </div>
                  </div>
                )}
                
                {analytics.pendingRequests > 0 && (
                  <div style={{
                    padding: '12px',
                    background: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fcd34d'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#92400e',
                      marginBottom: '8px'
                    }}>
                      🔔 Pending Action
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#78350f'
                    }}>
                      {analytics.pendingRequests} request{analytics.pendingRequests !== 1 ? 's' : ''} awaiting approval
                    </div>
                  </div>
                )}
                
                {analytics.lowStockItems.length === 0 && analytics.pendingRequests === 0 && analytics.mostRequestedBeans.length === 0 && (
                  <div style={{
                    padding: '12px',
                    background: '#d1fae5',
                    borderRadius: '8px',
                    border: '1px solid #a7f3d0'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#065f46',
                      marginBottom: '8px'
                    }}>
                      ✅ All Good
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#064e3b'
                    }}>
                      No immediate actions needed
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Most Active Users */}
          {analytics.mostActiveUsers.length > 0 && (
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2d3748',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaUser style={{ color: '#6366f1' }} />
                Most Active Users (Last 30 Days)
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {analytics.mostActiveUsers.map(([userName, requestCount], index) => (
                  <div key={userName} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        background: '#6366f1',
                        color: '#fff',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {index + 1}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#2d3748'
                      }}>
                        {userName}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6366f1'
                    }}>
                      {requestCount} requests
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#2d3748',
                margin: '0'
              }}>Add New Item</h2>
              <button 
                onClick={resetAddForm}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#718096',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px'
                }}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleAddItem} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Bean Name
                </label>
                <input 
                  type="text"
                  value={newItem.beanName}
                  onChange={(e) => setNewItem({ ...newItem, beanName: e.target.value })}
                  placeholder="e.g. Ethiopian Yirgacheffe"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5a3c';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Unit of Measure
                </label>
                <select 
                  value={newItem.unit}
                  onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    background: '#fff',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  {units.map(unit => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Size Per Unit
                </label>
                <input 
                  type="number"
                  value={newItem.unitSize}
                  onChange={(e) => setNewItem({ ...newItem, unitSize: e.target.value })}
                  placeholder={`e.g. 250 (for 250${newItem.unit} per unit)`}
                  min="0"
                  step="0.1"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5a3c';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
                <div style={{
                  fontSize: '12px',
                  color: '#718096',
                  marginTop: '4px'
                }}>
                  How much is in each unit? (e.g., 250g, 1kg, 500g per pack)
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button 
                  type="button"
                  onClick={resetAddForm}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: '2px solid #e9ecef',
                    background: '#fff',
                    color: '#718096',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={loading || !newItem.beanName.trim() || !newItem.unitSize}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: 'none',
                    background: loading || !newItem.beanName.trim() || !newItem.unitSize ? '#cbd5e0' : '#8b5a3c',
                    color: '#fff',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading || !newItem.beanName.trim() || !newItem.unitSize ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {loading ? 'Adding...' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {showEditModal && selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#2d3748',
                margin: '0'
              }}>Edit {selectedItem.beanName}</h2>
              <button 
                onClick={resetEditForm}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#718096',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px'
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{
                background: '#f7fafc',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#718096',
                  marginBottom: '4px'
                }}>Current Stock</div>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#2d3748'
                }}>
                  {selectedItem.quantity || 0} units
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#a0aec0',
                  marginTop: '4px'
                }}>
                  ({selectedItem.unitSize}{selectedItem.unit} per unit)
                </div>
              </div>

              <form onSubmit={handleEditQuantity}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '12px'
                  }}>
                    Action
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px'
                  }}>
                    <button 
                      type="button"
                      onClick={() => setEditForm({ ...editForm, action: 'add' })}
                      style={{
                        padding: '12px',
                        border: editForm.action === 'add' ? '2px solid #38a169' : '2px solid #e9ecef',
                        background: editForm.action === 'add' ? '#c6f6d5' : '#fff',
                        color: editForm.action === 'add' ? '#276749' : '#718096',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Add Stock
                    </button>
                    <button 
                      type="button"
                      onClick={() => setEditForm({ ...editForm, action: 'remove' })}
                      style={{
                        padding: '12px',
                        border: editForm.action === 'remove' ? '2px solid #e53e3e' : '2px solid #e9ecef',
                        background: editForm.action === 'remove' ? '#fed7d7' : '#fff',
                        color: editForm.action === 'remove' ? '#c53030' : '#718096',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Remove Stock
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Number of Units
                  </label>
                  <input 
                    type="number"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                    placeholder="Enter number of units"
                    min="0"
                    step="1"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5a3c';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Note (Optional)
                  </label>
                  <textarea 
                    value={editForm.note}
                    onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                    placeholder="e.g. Used for Gewan delivery"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5a3c';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                    }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button 
                    type="button"
                    onClick={resetEditForm}
                    style={{
                      flex: 1,
                      padding: '14px',
                      border: '2px solid #e9ecef',
                      background: '#fff',
                      color: '#718096',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={loading || !editForm.quantity}
                    style={{
                      flex: 1,
                      padding: '14px',
                      border: 'none',
                      background: loading || !editForm.quantity ? '#cbd5e0' : 
                        editForm.action === 'add' ? '#38a169' : '#e53e3e',
                      color: '#fff',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading || !editForm.quantity ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {loading ? 'Updating...' : 
                     editForm.action === 'add' ? 'Add Stock' : 'Remove Stock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            padding: '24px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>⚠️</div>
            
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#2d3748',
              margin: '0 0 12px 0'
            }}>Reset All Data?</h2>
            
            <p style={{
              fontSize: '16px',
              color: '#718096',
              margin: '0 0 8px 0',
              lineHeight: '1.5'
            }}>
              This will permanently delete:
            </p>
            
            <div style={{
              background: '#fed7d7',
              borderRadius: '8px',
              padding: '16px',
              margin: '16px 0',
              textAlign: 'left'
            }}>
              <ul style={{
                margin: '0',
                paddingLeft: '20px',
                fontSize: '14px',
                color: '#c53030'
              }}>
                <li>All beans requests</li>
                <li>All inventory logs</li>
                <li>All analytics data</li>
              </ul>
            </div>
            
            <div style={{
              background: '#c6f6d5',
              borderRadius: '8px',
              padding: '12px',
              margin: '16px 0',
              fontSize: '14px',
              color: '#276749'
            }}>
              ✅ <strong>Beans inventory will be preserved</strong>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: '#a0aec0',
              margin: '0 0 24px 0',
              fontStyle: 'italic'
            }}>
              This action cannot be undone.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowResetConfirm(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: '2px solid #e9ecef',
                  background: '#fff',
                  color: '#718096',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleResetAllData}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: 'none',
                  background: loading ? '#cbd5e0' : '#e53e3e',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? 'Resetting...' : 'Reset All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoasterInventoryPage;